import React from 'react';

import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import useUserHook from 'hooks/useUserHook';

import { PageNotFound } from 'components/common/ErrorPages/pageNotFound';
import GuardedRoute from 'components/common/GuardedRoute/guardedRoute';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import NoAccess from 'components/common/NoAccess/noAccess';
import { SideMenuListProps } from 'components/common/SideMenu/SideMenu';

import { riskConfig } from 'components/Dashboards/ExcellenceApps/riskConfig';
import { RedirectToLast } from 'components/Dashboards/Program/ProgramWrapper/redirectToLast';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Responsive } from 'components/Dashboards/responsive';
import { ChangeAnalysisPage } from 'components/pages/ChangeAnalysisPage';
import { CriticalPathAddedSinglePage } from 'components/pages/CriticalPathAddedSinglePage';
import { CriticalPathRemovedSinglePage } from 'components/pages/CriticalPathRemovedSinglePage';
import { DurationForecastPage } from 'components/pages/DurationForecastPage/DurationForecastPage';
import { FinishDateChangesSinglePage } from 'components/pages/FinishDateChangesSinglePage';
import { GanttPage } from 'components/pages/GanttPage';
import { IntegratedProgramDetailsPage } from 'components/pages/IntegratedProgramDetailsPage';
import { LargestAddedActivitiesSinglePage } from 'components/pages/LargestAddedActivitiesSinglePage';
import { LargestDurationVarianceSinglePage } from 'components/pages/LargestDurationVarianceSinglePage';
import { LargestFloatChangesSinglePage } from 'components/pages/LargestFloatChangesSinglePage';
import { LogicQualityCheckActivitiesPage } from 'components/pages/LogicQualityCheckActivitiesPage';
import { LogicQualityCheckCategoryPage } from 'components/pages/LogicQualityCheckCategoryPage';
import { LogicQualityCheckSummaryPage } from 'components/pages/LogicQualityCheckSummaryPage';
import { LogicQualityCheckValidationPage } from 'components/pages/LogicQualityCheckValidationPage';
import { ProgramLibraryPage } from 'components/pages/ProgramLibraryPage/ProgramLibraryPage';
import { ProjectPreferencesPage } from 'components/pages/ProjectPreferencesPage';
import { RelationshipCountChangesSinglePage } from 'components/pages/RelationshipCountChangesSinglePage';
import { RelationshipTypeChangesSinglePage } from 'components/pages/RelationshipTypeChangesSinglePage';
import { ScheduleSimulation } from 'components/pages/ScheduleSimulationPage';
import { StartDateChangesSinglePage } from 'components/pages/StartDateChangesSinglePage';
import { StatusChangesSinglePage } from 'components/pages/StatusChangesSinglePage';
import { SubProgramPreferencesPage } from 'components/pages/SubProgramPreferencesPage/SubProgramPreferencesPage';

export const sideMenu: SideMenuListProps[] = [
    {
        ...executionConfig.programLibrary,
        id: 'ProgramLibrary',
        muiIcon: <MuiIcon icon={'folder_open'} type={'round'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'ProgrammeLibrary',
    },
    {
        ...executionConfig.ganttChart,
        id: 'GanttChart',
        muiIcon: <MuiIcon icon={'view_timeline'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'Gantt',
    },
    {
        ...riskConfig.logicQualityCheck,
        id: 'LogicQualityCheck',
        muiIcon: <MuiIcon icon={'verified_user'} type={'outlined'} fontSize={'2.3rem'} color={'var(--text-primary)'} />,
        permissions: 'LogicChecker',
    },
    {
        ...riskConfig.durationForecast,
        id: 'DurationForecast',
        muiIcon: <MuiIcon icon={'hourglass_bottom'} type={'round'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'ClusterAnalysis',
    },
    {
        ...executionConfig.versionCompare,
        id: 'VersionCompare',
        muiIcon: <MuiIcon icon={'compare'} type={'round'} fontSize={'2.3rem'} color={'var(--text-primary)'} />,
        permissions: 'ChangeAnalysis',
    },
    {
        ...riskConfig.scheduleSimulation,
        id: 'ScheduleSimulation',
        muiIcon: <MuiIcon icon={'account_tree'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'ScheduleSimulation',
    },
];

export const PlanningRoutes = () => {
    const { path } = useRouteMatch();
    const { ability } = useUserHook();
    const redirect = sideMenu.find((item) => {
        return item.permissions && ability.can('view', item.permissions);
    });
    return (
        <Responsive sideMenu={sideMenu} title={'Planning'}>
            <Switch>
                <GuardedRoute
                    path={`${path}/${executionConfig.programLibrary.link}/${executionConfig.projectPreferences.link}`}
                    component={ProjectPreferencesPage}
                    auth={ability.can('view', 'ManageProjects')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.programLibrary.link}/${executionConfig.subProgramPreferences.link}`}
                    component={SubProgramPreferencesPage}
                    auth={ability.can('view', 'ManageProjects')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${riskConfig.logicQualityCheck.link}/:id/:category/:check/activities`}
                    component={LogicQualityCheckActivitiesPage}
                    auth={ability.can('view', 'LogicChecker')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.logicQualityCheck.link}/:id/:category/:check`}
                    component={LogicQualityCheckValidationPage}
                    auth={ability.can('view', 'LogicChecker')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.logicQualityCheck.link}/:id/:category`}
                    component={LogicQualityCheckCategoryPage}
                    auth={ability.can('view', 'LogicChecker')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.logicQualityCheck.link}/:id`}
                    component={LogicQualityCheckSummaryPage}
                    auth={ability.can('view', 'LogicChecker')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.logicQualityCheck.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${riskConfig.logicQualityCheck.link}`}
                            title={riskConfig.logicQualityCheck.title}
                        />
                    )}
                    auth={ability.can('view', 'LogicChecker')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.programLibrary.link}/${executionConfig.integratedProgramDetails.link}`}
                    component={IntegratedProgramDetailsPage}
                    auth={ability.can('view', 'ProgrammeLibrary')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.programLibrary.link}/:projectId?/:contractId?`}
                    component={ProgramLibraryPage}
                    auth={ability.can('view', 'ProgrammeLibrary')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.ganttChart.link}/:id`}
                    component={GanttPage}
                    auth={ability.can('view', 'Gantt')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.ganttChart.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.ganttChart.link}`}
                            title={executionConfig.ganttChart.title}
                        />
                    )}
                    auth={ability.can('view', 'Gantt')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.durationForecast.link}/:id`}
                    component={DurationForecastPage}
                    auth={ability.can('view', 'ClusterAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.durationForecast.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${riskConfig.durationForecast.link}`}
                            title={riskConfig.durationForecast.title}
                        />
                    )}
                    auth={ability.can('view', 'ClusterAnalysis')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={ChangeAnalysisPage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.statusChanges.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={StatusChangesSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.startDateChanges.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={StartDateChangesSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.finishDateChanges.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={FinishDateChangesSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.largestDurationVariance.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={LargestDurationVarianceSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.largestFloatChanges.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={LargestFloatChangesSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.criticalPathAdded.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={CriticalPathAddedSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.criticalPathRemoved.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={CriticalPathRemovedSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.relationshipTypeChanges.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={RelationshipTypeChangesSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.relationshipCountChanges.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={RelationshipCountChangesSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}/${executionConfig.largestAddedActivities.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={LargestAddedActivitiesSinglePage}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.versionCompare.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.versionCompare.link}`}
                            title={executionConfig.versionCompare.title}
                        />
                    )}
                    auth={ability.can('view', 'ChangeAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.scheduleSimulation.link}/:id`}
                    component={ScheduleSimulation}
                    auth={ability.can('view', 'ScheduleSimulation')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.scheduleSimulation.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${riskConfig.scheduleSimulation.link}`}
                            title={riskConfig.scheduleSimulation.title}
                        />
                    )}
                    auth={ability.can('view', 'ScheduleSimulation')}
                    redirect={`${path}/no-access`}
                />

                <Route exact path={`${path}/no-access`} component={NoAccess} />

                {/* default route */}
                <Route exact path={path} render={() => <Redirect to={`${path}/${redirect?.link}`} />} />
                <Route path={'*'} component={PageNotFound} />
            </Switch>
        </Responsive>
    );
};
